import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { StaffLayout } from '@/layouts/staff-layout';
import { StaffDashboard } from '@/features/staff/staff-dashboard';
import { STAFF_ROUTES } from '@/constants/staff-routes';
import { SubjectAreas } from '@/containers/SubjectAreas/subject-areas';
import { SubjectAreasGate } from '@/containers/SubjectAreas/subject-areas-gate';
import { Configurations } from '@/containers/Configurations/configurations';
import { CourseCatalog, AddCoursePage, ViewCoursePage } from '@/containers/CourseCatalog';
import {
  RecommendationFormPage,
  Recommendations,
  RecommendationsGate,
} from '@/containers/Recommendations';
import { ClustersAndPathways } from '@/containers/ClustersAndPathways/clusters-and-pathways';
import { PlansOfStudy } from '@/containers/PlanOfStudy/plans-of-study';
import { ListPageLayout } from '@/layout-templates';
import { NeonLoadingIndicator } from '@ps-refarch-ux/neon';
import { isEmbeddedInSucceed } from '@/utils/cp-context';
import { PlanOfStudyLegacyCatchAll } from '@/routes/plan-of-study-legacy-redirects';

const listPageLayoutElement = <ListPageLayout />;

const CourseCatalogGate = lazy(() =>
  import('@/containers/CourseCatalog').then((m) => ({ default: m.CourseCatalogGate }))
);

const CourseCatalogManageGate = lazy(() =>
  import('@/containers/CourseCatalog').then((m) => ({ default: m.CourseCatalogManageGate }))
);

const StudentCoursePlanManagerGate = lazy(() =>
  import('@/containers/StudentCoursePlanManager').then((m) => ({
    default: m.StudentCoursePlanManagerGate,
  }))
);

const courseCatalogGate = (
  <Suspense fallback={null}>
    <CourseCatalogGate />
  </Suspense>
);

const studentCoursePlanManagerGate = (
  <Suspense fallback={null}>
    <StudentCoursePlanManagerGate />
  </Suspense>
);

const courseCatalogManageGate = (
  <Suspense fallback={null}>
    <CourseCatalogManageGate />
  </Suspense>
);

const ConfigurationsGate = lazy(() =>
  import('@/containers/Configurations/configurations-gate').then((m) => ({
    default: m.ConfigurationsGate,
  }))
);

const configurationsGate = (
  <Suspense fallback={null}>
    <ConfigurationsGate />
  </Suspense>
);

const recommendationsGate = (
  <Suspense fallback={null}>
    <RecommendationsGate />
  </Suspense>
);

const PlansOfStudyGate = lazy(() =>
  import('@/containers/PlanOfStudy').then((m) => ({ default: m.PlansOfStudyGate }))
);
const PlansOfStudyManageGate = lazy(() =>
  import('@/containers/PlanOfStudy').then((m) => ({ default: m.PlansOfStudyManageGate }))
);
const ClustersAndPathwaysGate = lazy(() =>
  import('@/containers/PlanOfStudy').then((m) => ({ default: m.ClustersAndPathwaysGate }))
);
const ClustersAndPathwaysManageGate = lazy(() =>
  import('@/containers/PlanOfStudy').then((m) => ({ default: m.ClustersAndPathwaysManageGate }))
);
const CreatePlanPage = lazy(() =>
  import('@/containers/PlanOfStudy').then((m) => ({ default: m.CreatePlanPage }))
);
const PlanOfStudyDetail = lazy(() =>
  import('@/containers/PlanOfStudy').then((m) => ({
    default: m.PlanOfStudyDetail,
  }))
);
const EditPlanPage = lazy(() =>
  import('@/containers/PlanOfStudy').then((m) => ({ default: m.EditPlanPage }))
);
const CreateRequirementPage = lazy(() =>
  import('@/containers/PlanOfStudy').then((m) => ({ default: m.CreateRequirementPage }))
);
const CreateAreaPage = lazy(() =>
  import('@/containers/ClustersAndPathways/create-area-page').then((m) => ({
    default: m.CreateAreaPage,
  }))
);
const AreaDetailPage = lazy(() =>
  import('@/containers/ClustersAndPathways/area-detail-page').then((m) => ({
    default: m.AreaDetailPage,
  }))
);
const CreateSpecializationPage = lazy(() =>
  import('@/containers/ClustersAndPathways/create-specialization-page').then((m) => ({
    default: m.CreateSpecializationPage,
  }))
);
const SpecializationDetailPage = lazy(() =>
  import('@/containers/ClustersAndPathways/specialization-detail-page').then((m) => ({
    default: m.SpecializationDetailPage,
  }))
);
const PlanRequirementDetailPage = lazy(() =>
  import('@/containers/PlanOfStudy').then((m) => ({ default: m.PlanRequirementDetailPage }))
);

const planOfStudyGate = (
  <Suspense fallback={null}>
    <PlansOfStudyGate />
  </Suspense>
);

const planOfStudyManageGate = (
  <Suspense fallback={null}>
    <PlansOfStudyManageGate />
  </Suspense>
);

const clustersAndPathwaysGate = (
  <Suspense fallback={null}>
    <ClustersAndPathwaysGate />
  </Suspense>
);

const clustersAndPathwaysManageGate = (
  <Suspense fallback={null}>
    <ClustersAndPathwaysManageGate />
  </Suspense>
);

export function StaffRoutes() {
  const {
    subjectArea,
    plansOfStudy,
    courseCatalog,
    clusterAndPathway,
    configurations,
    studentCoursePlans,
    recommendations,
  } = STAFF_ROUTES;
  const defaultRedirect = isEmbeddedInSucceed() ? `/${courseCatalog.list}` : '/staff';
  return (
    <Routes>
      <Route path="/courses/subject-area" element={<StaffLayout />}>
        <Route element={courseCatalogGate}>
          <Route element={<SubjectAreasGate />}>
            <Route element={listPageLayoutElement}>
              <Route index element={<SubjectAreas />} />
            </Route>
          </Route>
        </Route>
      </Route>
      <Route path={`/${plansOfStudy.list}`} element={<StaffLayout />}>
        <Route element={planOfStudyGate}>
          <Route element={listPageLayoutElement}>
            <Route index element={<PlansOfStudy />} />
          </Route>
          <Route path=":id" element={<PlanOfStudyDetail />} />
          <Route element={planOfStudyManageGate}>
            <Route path="create" element={<CreatePlanPage />} />
            <Route path=":id/edit" element={<EditPlanPage />} />
          </Route>
        </Route>
      </Route>
      <Route path="/courses/requirements" element={<StaffLayout />}>
        <Route element={planOfStudyGate}>
          <Route index element={<Navigate to={`/${plansOfStudy.list}`} replace />} />
          <Route path=":requirementId" element={<PlanRequirementDetailPage />} />
          <Route element={planOfStudyManageGate}>
            <Route path="create" element={<CreateRequirementPage />} />
          </Route>
        </Route>
      </Route>
      <Route path="/courses/plans-of-study/*" element={<StaffLayout />}>
        <Route path="*" element={<PlanOfStudyLegacyCatchAll />} />
      </Route>
      <Route path="/courses/configurations" element={<StaffLayout />}>
        <Route element={configurationsGate}>
          <Route index element={<Configurations />} />
        </Route>
      </Route>
      <Route path="/courses/student-course-plans" element={<StaffLayout />}>
        <Route element={listPageLayoutElement}>
          <Route index element={studentCoursePlanManagerGate} />
        </Route>
      </Route>
      <Route path="/courses/course-catalog" element={<StaffLayout />}>
        <Route element={courseCatalogGate}>
          <Route element={listPageLayoutElement}>
            <Route index element={<CourseCatalog />} />
          </Route>
          <Route path="view/:id" element={<ViewCoursePage />} />
          <Route element={courseCatalogManageGate}>
            <Route path="add" element={<AddCoursePage />} />
            <Route path="edit/:id" element={<AddCoursePage />} />
          </Route>
        </Route>
      </Route>
      <Route path={`/${recommendations.list}`} element={<StaffLayout />}>
        <Route element={recommendationsGate}>
          <Route element={listPageLayoutElement}>
            <Route index element={<Recommendations />} />
          </Route>
          <Route path="add" element={<RecommendationFormPage />} />
          <Route path=":courseId/edit" element={<RecommendationFormPage />} />
        </Route>
      </Route>
      <Route path={`/${clusterAndPathway.list}`} element={<StaffLayout />}>
        <Route element={clustersAndPathwaysGate}>
          <Route element={listPageLayoutElement}>
            <Route index element={<ClustersAndPathways />} />
          </Route>
          <Route
            path=":clusterId/specializations/:specializationId"
            element={
              <Suspense fallback={<NeonLoadingIndicator dataSize="large" />}>
                <SpecializationDetailPage />
              </Suspense>
            }
          />
          <Route path=":id" element={<AreaDetailPage />} />
          <Route element={clustersAndPathwaysManageGate}>
            <Route path="create" element={<CreateAreaPage />} />
            <Route path=":id/edit" element={<CreateAreaPage />} />
            <Route
              path=":clusterId/specializations/create"
              element={
                <Suspense fallback={<NeonLoadingIndicator dataSize="large" />}>
                  <CreateSpecializationPage />
                </Suspense>
              }
            />
          </Route>
        </Route>
      </Route>
      <Route path="/staff" element={<StaffLayout />}>
        <Route index element={<Navigate to={subjectArea.list} replace />} />
        <Route path="dashboard" element={<StaffDashboard />} />
        <Route path={subjectArea.list} element={courseCatalogGate}>
          <Route element={listPageLayoutElement}>
            <Route index element={<SubjectAreas />} />
          </Route>
        </Route>
        <Route path={configurations.list} element={configurationsGate}>
          <Route index element={<Configurations />} />
        </Route>
        <Route path={studentCoursePlans.list} element={listPageLayoutElement}>
          <Route index element={studentCoursePlanManagerGate} />
        </Route>
        <Route path={courseCatalog.list} element={courseCatalogGate}>
          <Route element={listPageLayoutElement}>
            <Route index element={<CourseCatalog />} />
          </Route>
          <Route path="view/:id" element={<ViewCoursePage />} />
          <Route element={courseCatalogManageGate}>
            <Route path="add" element={<AddCoursePage />} />
            <Route path="edit/:id" element={<AddCoursePage />} />
          </Route>
        </Route>
        <Route path={recommendations.list} element={recommendationsGate}>
          <Route element={listPageLayoutElement}>
            <Route index element={<Recommendations />} />
          </Route>
        </Route>
        <Route path={clusterAndPathway.list} element={clustersAndPathwaysGate}>
          <Route element={listPageLayoutElement}>
            <Route index element={<ClustersAndPathways />} />
          </Route>
          <Route
            path=":clusterId/specializations/:specializationId"
            element={
              <Suspense fallback={<NeonLoadingIndicator dataSize="large" />}>
                <SpecializationDetailPage />
              </Suspense>
            }
          />
          <Route path=":id" element={<AreaDetailPage />} />
          <Route element={clustersAndPathwaysManageGate}>
            <Route path="create" element={<CreateAreaPage />} />
            <Route path=":id/edit" element={<CreateAreaPage />} />
            <Route
              path=":clusterId/specializations/create"
              element={
                <Suspense fallback={<NeonLoadingIndicator dataSize="large" />}>
                  <CreateSpecializationPage />
                </Suspense>
              }
            />
          </Route>
        </Route>
        <Route path={plansOfStudy.list} element={planOfStudyGate}>
          <Route element={listPageLayoutElement}>
            <Route index element={<PlansOfStudy />} />
          </Route>
          <Route path=":id" element={<PlanOfStudyDetail />} />
          <Route element={planOfStudyManageGate}>
            <Route path="create" element={<CreatePlanPage />} />
            <Route path=":id/edit" element={<EditPlanPage />} />
          </Route>
        </Route>
        <Route path="courses/requirements" element={planOfStudyGate}>
          <Route index element={<Navigate to={`/${plansOfStudy.list}`} replace />} />
          <Route path=":requirementId" element={<PlanRequirementDetailPage />} />
          <Route element={planOfStudyManageGate}>
            <Route path="create" element={<CreateRequirementPage />} />
          </Route>
        </Route>
        <Route path="courses/plans-of-study/*" element={<PlanOfStudyLegacyCatchAll />} />
      </Route>
      <Route path="/" element={<Navigate to={defaultRedirect} replace />} />
      <Route path="*" element={<Navigate to={defaultRedirect} replace />} />
    </Routes>
  );
}
